import { InputField } from '@design-system';
import { AddressElement } from '@stripe/react-stripe-js';
import { customsCodeIsRequired } from '@utils/address';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import { COUNTRY_CODES } from '../../../../constants/locations';
import {
  DetailsAddressState,
  DetailsAddressValue,
} from './DetailsAddressFields.state';

const allowedCountries = COUNTRY_CODES.filter(Boolean);

type StripeAddressState = {
  complete: boolean;
  value: DetailsAddressValue | null;
};

export type DetailsAddressFieldsProps = {
  mode?: 'shipping' | 'billing';
  onChange: (state: DetailsAddressState) => void;
};

export const DetailsAddressFields = ({
  mode = 'shipping',
  onChange,
}: DetailsAddressFieldsProps) => {
  const { t } = useTranslation(['product', 'countries']);
  const [stripeState, setStripeState] = useState<StripeAddressState>({
    complete: false,
    value: null,
  });
  const [customsCode, setCustomsCode] = useState('');

  const country = stripeState.value?.address?.country;
  const isCustomsCodeRequired =
    mode === 'shipping' && customsCodeIsRequired(country);

  const emitChange = (stripe: StripeAddressState, rawCustomsCode: string) => {
    const trimmedCustomsCode = rawCustomsCode.trim();
    const customsRequired =
      mode === 'shipping' &&
      customsCodeIsRequired(stripe.value?.address?.country);

    onChange({
      complete:
        stripe.complete && (!customsRequired || trimmedCustomsCode.length > 0),
      customsCode: trimmedCustomsCode || null,
      value: stripe.value,
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <AddressElement
        onChange={(event) => {
          const nextStripeState = {
            complete: event.complete,
            value: event.value,
          };
          setStripeState(nextStripeState);
          emitChange(nextStripeState, customsCode);
        }}
        options={{
          ...(mode === 'shipping' ? { allowedCountries } : {}),
          mode,
          autocomplete: {
            mode: 'automatic',
          },
          display: {
            name: 'full',
          },
          fields: {
            phone: 'always',
          },
          validation: {
            phone: {
              required: 'always',
            },
          },
        }}
      />

      {country && isCustomsCodeRequired && (
        <div>
          <InputField
            id="details-customs-code"
            label={t(`product:drawers.shipping.customs.labels.${country}`)}
            name="customsCode"
            onChange={(e) => {
              const nextCustomsCode = e.currentTarget.value;
              setCustomsCode(nextCustomsCode);
              emitChange(stripeState, nextCustomsCode);
            }}
            value={customsCode}
          />
          <p className="mt-2 text-xs text-neutral-4">
            {t('product:drawers.shipping.customs.text', {
              country: `${t(`countries:${country}`)}`,
              label: t(`product:drawers.shipping.customs.labels.${country}`),
            })}
          </p>
        </div>
      )}
    </div>
  );
};
